import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Phone, ArrowRight } from 'lucide-react';

export const FAQPage = () => {
  const faqs = [
    {
      question: "What does a title company do?",
      answer: "A title company searches public records to verify property ownership, identifies any liens or claims against the property, and issues title insurance to protect buyers and lenders. We also coordinate closings, prepare documents, and ensure funds are properly distributed. Think of us as the neutral party making sure everything is legitimate and everyone is protected."
    },
    {
      question: "Who chooses the title company?",
      answer: "It depends on the location. In Pennsylvania, Central New Jersey, and South Florida, the buyer typically chooses the title company. In North Jersey, North Florida, and most other locations, the seller usually selects the title company. Every transaction can be different, so if you're unsure who has the choice in your deal, I'm happy to advise – just reach out."
    },
    {
      question: "What is title insurance and why do I need it?",
      answer: "Title insurance protects against hidden problems with the property's title that weren't discovered during the title search. This could include unknown heirs, forged documents, recording errors, or liens that weren't properly disclosed. Unlike other insurance that protects against future events, title insurance protects against past events that could affect your ownership. It's a one-time premium that protects you for as long as you own the property."
    },
    {
      question: "What's the difference between owner's and lender's title insurance?",
      answer: "Lender's title insurance protects the mortgage company's interest in the property – it's typically required when you get a loan. Owner's title insurance protects your investment in the home. While lender's insurance only covers the loan amount (which decreases over time), owner's insurance covers your full purchase price and lasts as long as you or your heirs own the property."
    },
    {
      question: "How early should title be ordered?",
      answer: "The sooner, the better! Ideally, title should be ordered as soon as the sales agreement is signed. This gives us time to complete the search, identify any issues, and resolve them before closing. Rushing the title process can lead to delays or missed problems. I recommend ordering title within 24-48 hours of a signed agreement."
    },
    {
      question: "What happens if an issue comes up before closing?",
      answer: "Don't panic – issues are common and usually solvable. Common problems include liens from unpaid taxes or contractors, boundary disputes, or errors in public records. When I find an issue, I work with all parties to resolve it. Sometimes it's a simple paperwork fix; other times it requires negotiation. My job is to identify problems early and solve them so they don't delay your closing."
    },
    {
      question: "How long does the title process take?",
      answer: "Timing depends on the transaction type. For a residential purchase, expect 5-7 days. Commercial properties typically take 2 weeks or longer due to additional complexity. Residential refinances are usually completed in 3-5 days. Rush orders are handled on a case-by-case basis – if you have a tight timeline, let me know and I'll do everything I can to accommodate."
    },
    {
      question: "What should I bring to closing?",
      answer: "Bring a valid government-issued photo ID (driver's license or passport), any documents your lender has requested, and a cashier's check or wire transfer confirmation for your closing costs if required. I'll provide a detailed closing disclosure beforehand so you know exactly what funds are needed. If you're unsure about anything, just ask – I'm happy to walk you through it."
    },
    {
      question: "Do you handle commercial transactions?",
      answer: "Yes! I handle both residential and commercial title work. Commercial transactions often involve additional complexity – entity verification, zoning reviews, survey matters, and larger transaction amounts. My experience with both types of deals means I understand the unique requirements of commercial closings and can guide you through the process."
    },
    {
      question: "What areas do you serve?",
      answer: "I serve clients in Pennsylvania, New Jersey, Florida, and New York. In PA, I cover Philadelphia and surrounding counties including Montgomery, Delaware, Bucks, and Chester. In New Jersey, I handle transactions throughout the state. I also serve clients in Florida and New York. If you have a property in any of these states, I'd be happy to help."
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-amber-700 font-medium tracking-wide uppercase text-sm mb-4">FAQ</p>
          <h1 
            className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-slate-900 mb-6 max-w-3xl mx-auto"
            data-testid="faq-title"
          >
            Common Questions, <span className="text-amber-700">Clear Answers</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Title insurance doesn't have to be confusing. Here are answers to the questions 
            I hear most often from clients.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-slate-50 rounded-xl border-none px-6 data-[state=open]:bg-white data-[state=open]:shadow-lg transition-all"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left py-6 hover:no-underline">
                  <span className="font-['Playfair_Display'] text-lg font-semibold text-slate-900 pr-4">
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-6">
                  <p className="text-slate-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Still Have Questions Section */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="faq-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
            Still Have Questions?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            I'm always happy to explain things in more detail. Reach out anytime – 
            no question is too basic or too complicated.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" data-testid="faq-cta-primary">
              <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 py-4 font-medium transition-all hover:scale-105">
                <Phone className="w-5 h-5 mr-2" />
                Ask Me Directly
              </Button>
            </Link>
            <Link to="/services" data-testid="faq-cta-secondary">
              <Button 
                variant="outline" 
                className="border-2 border-slate-200 hover:border-slate-900 rounded-full px-8 py-4 font-medium"
              >
                View Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
